<template>
    <div v-if="isVisible" class="col-span-3">
        <h4 class="text-sm font-semibold text-gray-700">
            {{ roleGroup === 'customer' ? 'Kundenunternehmen' : 'Dienstleisterunternehmen' }}
        </h4>
        <p class="mt-1 text-xs leading-5 text-gray-500">
            <template v-if="roleGroup === 'customer'">
                Optional können Sie den Kunden einem oder mehreren Kundenunternehmen zuordnen. Privatkunden bleiben ohne Unternehmenszuordnung und werden über Leistungskonten verwaltet.
            </template>
            <template v-else>
                Ordnen Sie den Account genau einem Dienstleisterunternehmen zu.
            </template>
        </p>

        <div v-if="roleGroup === 'customer'" class="form-selection-panel mt-2 overflow-hidden">
            <div v-if="eligibleCompanies.length > 0" class="overflow-x-auto">
                <table class="min-w-full">
                    <thead>
                        <tr class="border-b border-gray-300 text-left text-sm font-normal text-gray-700">
                            <th class="px-3 py-2 font-normal">Unternehmen</th>
                            <th class="px-3 py-2 font-normal whitespace-nowrap">Primärunternehmen</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="company in eligibleCompanies"
                            :key="'user-customer-company-' + company.id"
                            class="border-b border-gray-300 last:border-b-0"
                        >
                            <td class="px-3 py-2">
                                <label class="flex items-center cursor-pointer">
                                    <input
                                        :checked="isSelected(company.id)"
                                        class="form-checkbox h-4 w-4 text-gray-700 mr-3"
                                        type="checkbox"
                                        @change="toggleCustomerCompany(company.id, $event.target.checked)"
                                    >
                                    <img
                                        :src="company.image || '/images/default/icon.png'"
                                        :alt="company.name"
                                        class="h-8 w-8 rounded object-cover border border-gray-200 mr-3 flex-shrink-0"
                                    >
                                    <span class="text-sm text-gray-700 truncate">{{ company.name }}</span>
                                </label>
                            </td>
                            <td class="px-3 py-2">
                                <label class="inline-flex items-center gap-2 text-xs text-gray-500 cursor-pointer">
                                    <input
                                        :checked="isPrimary(company.id)"
                                        :disabled="!isSelected(company.id)"
                                        :value="company.id"
                                        class="form-radio h-4 w-4 text-gray-700"
                                        name="primary-customer-company"
                                        type="radio"
                                        @change="setPrimaryCompany(company.id)"
                                    >
                                    <span>Primär</span>
                                </label>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div v-else class="px-3 py-3 text-sm text-gray-500">
                Es sind keine Kundenunternehmen verfügbar.
            </div>
        </div>

        <p v-if="roleGroup === 'customer'" class="mt-2 text-xs text-gray-500">
            Das Primärunternehmen wird in Übersichten und als Standardbezug für Unternehmensstammdaten verwendet.
        </p>

        <div v-if="roleGroup === 'service_provider'" class="mt-2 relative rounded shadow-sm">
            <select
                :value="normalizedAssignment.companyId || ''"
                class="block form-select w-full py-2 px-3 border border-gray-300 bg-white rounded shadow-sm focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                required
                @change="setProviderCompany($event.target.value)"
            >
                <option value="" disabled>Unternehmen wählen</option>
                <option v-for="company in eligibleCompanies" :key="'user-provider-company-' + company.id" :value="company.id">
                    {{ company.name }}
                </option>
            </select>
            <p v-if="eligibleCompanies.length < 1" class="mt-2 text-sm text-gray-500">
                Es sind keine Dienstleisterunternehmen verfügbar.
            </p>
        </div>
    </div>
</template>

<script>
import {
    normalizeCompanyAssignment,
    normalizeCompanyId,
    normalizeUserCompanyAssignment,
    companyMatchesRole,
} from '@/utils/user-company-assignment';

export default {
    name: 'user-company-assignment',
    props: {
        roleGroup: {type: String, default: ''},
        companies: {type: Array, default: () => []},
        companyId: {type: [Number, String], default: null},
        companyIds: {type: Array, default: () => []},
        companyAssignments: {type: Array, default: () => []},
    },
    emits: ['update:companyId', 'update:companyIds', 'update:companyAssignments'],
    computed: {
        isVisible() {
            return ['customer', 'service_provider'].includes(this.roleGroup);
        },
        eligibleCompanies() {
            return this.companies.filter((company) => companyMatchesRole(company, this.roleGroup));
        },
        normalizedAssignment() {
            return normalizeUserCompanyAssignment(
                this.roleGroup,
                this.companyId,
                this.companyIds,
                this.companies,
                this.companyAssignments
            );
        },
    },
    methods: {
        emitAssignment(assignment) {
            this.$emit('update:companyId', assignment.companyId);
            this.$emit('update:companyIds', assignment.companyIds);
            this.$emit('update:companyAssignments', assignment.companyAssignments || []);
        },
        assignmentFor(companyId) {
            const normalizedCompanyId = normalizeCompanyId(companyId);
            return this.normalizedAssignment.companyAssignments.find(
                (assignment) => assignment.company_id === normalizedCompanyId
            ) || null;
        },
        isSelected(companyId) {
            return this.assignmentFor(companyId) !== null;
        },
        isPrimary(companyId) {
            return this.normalizedAssignment.companyId === normalizeCompanyId(companyId);
        },
        toggleCustomerCompany(value, checked) {
            const companyId = normalizeCompanyId(value);
            if (!companyId) {
                return;
            }

            const assignments = checked
                ? [...this.normalizedAssignment.companyAssignments, normalizeCompanyAssignment({company_id: companyId})]
                : this.normalizedAssignment.companyAssignments.filter((assignment) => assignment.company_id !== companyId);

            this.emitAssignment(normalizeUserCompanyAssignment(
                this.roleGroup,
                this.normalizedAssignment.companyId,
                [],
                this.companies,
                assignments
            ));
        },
        setPrimaryCompany(value) {
            const companyId = normalizeCompanyId(value);
            if (!companyId || !this.isSelected(companyId)) {
                return;
            }

            this.emitAssignment(normalizeUserCompanyAssignment(
                this.roleGroup,
                companyId,
                [],
                this.companies,
                this.normalizedAssignment.companyAssignments
            ));
        },
        setProviderCompany(value) {
            const companyId = normalizeCompanyId(value);
            this.emitAssignment({
                companyId,
                companyIds: companyId ? [companyId] : [],
                companyAssignments: [],
            });
        },
    },
};
</script>
